import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertCircle,
  CheckCircle2,
  Filter,
  MessageSquare,
  Phone,
  Search,
  Send,
  X,
} from 'lucide-react';
import {
  BosDurum,
  Buton,
  Girdi,
  Iskelet,
  Rozet,
  VeriTablo,
  toast,
  type ColumnDef,
  type RozetTonu,
} from '@emanetx/ui';
import {
  destekDurumGuncelle,
  destekMesajEkle,
  destekTalebiGetir,
  destekTalepleriniListele,
  type DestekDurumu,
  type DestekTalebi,
} from '@emanetx/sdk';
import { istemci } from '../istemci.js';

const DURUM_TONU: Record<DestekDurumu, RozetTonu> = {
  ACIK: 'kirmizi',
  CEVAP_BEKLIYOR: 'turuncu',
  COZULDU: 'yesil',
  KAPATILDI: 'gri',
};

const DURUM_ETIKET: Record<DestekDurumu, string> = {
  ACIK: 'Açık',
  CEVAP_BEKLIYOR: 'Cevap bekliyor',
  COZULDU: 'Çözüldü',
  KAPATILDI: 'Kapatıldı',
};

export function SayfaDestek() {
  const [sayfa, setSayfa] = useState(1);
  const [arama, setArama] = useState('');
  const [durum, setDurum] = useState<DestekDurumu | ''>('');
  const [acikTalepId, setAcikTalepId] = useState<string | null>(null);

  const sorgu = {
    sayfa,
    boyut: 50,
    ...(arama && { arama }),
    ...(durum && { durum: durum as DestekDurumu }),
  };

  const liste = useQuery({
    queryKey: ['destek', 'liste', sorgu],
    queryFn: () => destekTalepleriniListele(istemci, sorgu),
    placeholderData: (o) => o,
  });

  const kolonlar = useMemo<ColumnDef<DestekTalebi, unknown>[]>(
    () => [
      {
        accessorKey: 'konu',
        header: 'Konu',
        meta: { etiket: 'Konu' },
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.konu}</div>
            <div className="text-fume-500 line-clamp-1 max-w-md text-xs">
              {row.original.aciklama}
            </div>
          </div>
        ),
      },
      {
        id: 'musteri',
        header: 'Müşteri',
        meta: { etiket: 'Müşteri' },
        enableSorting: false,
        cell: ({ row }) => (
          <div>
            <div className="text-sm">{row.original.musteriAdSoyad ?? 'Misafir'}</div>
            <div className="text-fume-500 flex items-center gap-1 font-mono text-xs">
              <Phone size={10} /> {row.original.telefon}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'durum',
        header: 'Durum',
        meta: { etiket: 'Durum' },
        cell: ({ row }) => (
          <Rozet ton={DURUM_TONU[row.original.durum]} nokta>
            {DURUM_ETIKET[row.original.durum]}
          </Rozet>
        ),
      },
      {
        accessorKey: 'oncelik',
        header: 'Öncelik',
        meta: { etiket: 'Öncelik' },
        cell: ({ row }) => <span className="font-mono text-xs">P{row.original.oncelik}</span>,
      },
      {
        id: 'mesaj',
        header: 'Mesaj',
        meta: { etiket: 'Mesaj' },
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-fume-500 inline-flex items-center gap-1 font-mono text-xs">
            <MessageSquare size={11} /> {row.original.mesajSayisi ?? 0}
          </span>
        ),
      },
      {
        accessorKey: 'guncellemeTarihi',
        header: 'Son aktivite',
        meta: { etiket: 'Son aktivite' },
        cell: ({ row }) => (
          <span className="text-fume-500 font-mono text-xs">
            {new Date(row.original.guncellemeTarihi).toLocaleString('tr-TR', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Destek Talepleri</h1>
        <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
          Müşterilerin açtığı destek talepleri ve mesaj geçmişi.
        </p>
      </header>

      <VeriTablo
        veri={liste.data?.veriler ?? []}
        kolonlar={kolonlar}
        toplam={liste.data?.toplam}
        sayfa={sayfa}
        boyut={50}
        onSayfa={setSayfa}
        yukleniyor={liste.isLoading}
        yenileniyor={liste.isFetching}
        onYenile={() => void liste.refetch()}
        onSatirTikla={(t) => setAcikTalepId(t.id)}
        bosDurum={
          <BosDurum
            ikon={AlertCircle}
            baslik={arama || durum ? 'Eşleşen talep yok' : 'Henüz destek talebi yok'}
            aciklama="Müşteri uygulamasından gelen talepler burada görünür."
          />
        }
        ustAksiyonlar={
          <>
            <div className="relative min-w-0 flex-1">
              <Search
                size={14}
                className="text-fume-400 pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
              />
              <Girdi
                placeholder="Konu, telefon veya müşteri ara..."
                value={arama}
                onChange={(e) => {
                  setArama(e.target.value);
                  setSayfa(1);
                }}
                className="h-10 pl-9 text-xs"
              />
            </div>
            <Filter size={13} className="text-fume-400" />
            <select
              value={durum}
              onChange={(e) => {
                setDurum(e.target.value as DestekDurumu | '');
                setSayfa(1);
              }}
              className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 h-10 rounded-lg border bg-white px-3 text-xs"
            >
              <option value="">Tümü</option>
              <option value="ACIK">Açık</option>
              <option value="CEVAP_BEKLIYOR">Cevap bekliyor</option>
              <option value="COZULDU">Çözüldü</option>
              <option value="KAPATILDI">Kapatıldı</option>
            </select>
          </>
        }
      />

      <DestekDetayDialog talepId={acikTalepId} onKapat={() => setAcikTalepId(null)} />
    </div>
  );
}

function DestekDetayDialog({ talepId, onKapat }: { talepId: string | null; onKapat: () => void }) {
  const qc = useQueryClient();
  const acik = !!talepId;
  const [yanit, setYanit] = useState('');

  const detay = useQuery({
    queryKey: ['destek', 'detay', talepId],
    queryFn: () => destekTalebiGetir(istemci, talepId as string),
    enabled: acik,
  });

  const durumDegistir = useMutation({
    mutationFn: (durum: DestekDurumu) => destekDurumGuncelle(istemci, talepId as string, durum),
    onSuccess: () => {
      toast.success('Durum güncellendi');
      void qc.invalidateQueries({ queryKey: ['destek'] });
    },
  });

  const mesajGonder = useMutation({
    mutationFn: () => destekMesajEkle(istemci, talepId as string, yanit.trim()),
    onSuccess: () => {
      setYanit('');
      toast.success('Yanıt gönderildi');
      void qc.invalidateQueries({ queryKey: ['destek'] });
    },
    onError: () => toast.error('Gönderilemedi'),
  });

  return (
    <Dialog.Root open={acik} onOpenChange={(o) => !o && onKapat()}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-fume-900/60 fixed inset-0 z-40 backdrop-blur-sm" />
        <Dialog.Content className="dark:bg-fume-900 fixed inset-4 z-50 flex flex-col rounded-2xl bg-white shadow-2xl sm:inset-8">
          <div className="border-fume-100 dark:border-fume-800 flex items-center justify-between border-b px-6 py-3">
            <Dialog.Title className="text-fume-500 text-sm font-medium">Destek Talebi</Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Kapat" className="text-fume-400 hover:text-fume-700">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {detay.isLoading ? (
              <Iskelet className="h-64" />
            ) : !detay.data ? (
              <p className="text-fume-500 text-sm">Talep bulunamadı.</p>
            ) : (
              <div className="mx-auto max-w-3xl space-y-5">
                <header>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl font-bold">{detay.data.konu}</h2>
                    <Rozet ton={DURUM_TONU[detay.data.durum]} nokta>
                      {DURUM_ETIKET[detay.data.durum]}
                    </Rozet>
                  </div>
                  <p className="text-fume-500 mt-2 text-sm">
                    {detay.data.musteriAdSoyad ?? 'Misafir'} ·{' '}
                    <span className="font-mono">{detay.data.telefon}</span>
                  </p>
                  <p className="bg-fume-50 dark:bg-fume-800/40 mt-3 whitespace-pre-wrap rounded-lg p-3 text-sm">
                    {detay.data.aciklama}
                  </p>
                </header>

                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-fume-500 text-xs">Durumu değiştir:</span>
                  {(['ACIK', 'CEVAP_BEKLIYOR', 'COZULDU', 'KAPATILDI'] as DestekDurumu[]).map(
                    (d) => (
                      <Buton
                        key={d}
                        stil={detay.data!.durum === d ? 'birincil' : 'cizgili'}
                        boyut="kucuk"
                        onClick={() => durumDegistir.mutate(d)}
                        disabled={durumDegistir.isPending}
                      >
                        {DURUM_ETIKET[d]}
                      </Buton>
                    ),
                  )}
                </div>

                <section>
                  <h3 className="mb-2 text-sm font-semibold">Mesajlar</h3>
                  {detay.data.mesajlar.length === 0 ? (
                    <p className="text-fume-500 text-sm">Henüz mesaj yok.</p>
                  ) : (
                    <ul className="space-y-3">
                      {detay.data.mesajlar.map((m) => {
                        const admin = m.yazanTipi === 'admin';
                        return (
                          <li
                            key={m.id}
                            className={`max-w-[80%] rounded-lg p-3 ${
                              admin
                                ? 'bg-marka-100 dark:bg-marka-900/30 ml-auto'
                                : 'bg-fume-100 dark:bg-fume-800 mr-auto'
                            }`}
                          >
                            <div className="mb-1 flex items-center justify-between gap-3 text-xs">
                              <span className="font-medium">
                                {admin ? (m.yazanAdSoyad ?? 'Admin') : 'Müşteri'}
                              </span>
                              <span className="text-fume-400 font-mono">
                                {new Date(m.olusturmaTarihi).toLocaleString('tr-TR', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </div>
                            <p className="whitespace-pre-wrap text-sm">{m.govde}</p>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>

                <section className="border-fume-100 dark:border-fume-800 border-t pt-4">
                  <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                    <CheckCircle2 size={14} className="text-marka-500" /> Yanıt yaz
                  </h3>
                  <textarea
                    value={yanit}
                    onChange={(e) => setYanit(e.target.value)}
                    rows={4}
                    placeholder="Cevabınızı yazın..."
                    className="border-fume-200 dark:border-fume-700 dark:bg-fume-900 dark:text-fume-50 w-full rounded-lg border bg-white px-3 py-2 text-sm"
                  />
                  <div className="mt-2 flex justify-end">
                    <Buton
                      onClick={() => mesajGonder.mutate()}
                      disabled={!yanit.trim() || mesajGonder.isPending}
                    >
                      <Send size={14} /> {mesajGonder.isPending ? 'Gönderiliyor...' : 'Gönder'}
                    </Buton>
                  </div>
                </section>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
